import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaMoneyBillWave, 
  FaProjectDiagram, 
  FaUser, 
  FaCalendarAlt,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const FiltersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    h3 {
      margin: 0;
      color: #141340;
      font-size: 1.1rem;
    }
  }
  
  .filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  font-size: 0.9rem;
  color: #141340;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const RequestsTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 2fr 1fr 1fr auto;
    
    .date-column {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr auto;
    
    .amount-column,
    .requester-column {
      display: none;
    }
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f1f1f1;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(246, 139, 33, 0.02);
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 2fr 1fr 1fr auto;
    
    .date-column {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr auto;
    
    .amount-column,
    .requester-column {
      display: none;
    }
  }
`;

const RequestId = styled.div`
  font-weight: 500;
  color: #141340;
  font-size: 0.9rem;
  text-align: left;
`;

const ProjectName = styled.div`
  color: #495057;
  font-size: 0.9rem;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const Requester = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  text-align: left;
`;

const Amount = styled.div`
  font-weight: 600;
  color: #F68B21;
  font-size: 0.9rem;
  text-align: right;
`;

const Date = styled.div`
  color: #6c757d;
  font-size: 0.85rem;
  text-align: left;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  
  ${props => {
    switch (props.status.toLowerCase()) {
      case 'pending':
        return `
          background: rgba(255, 193, 7, 0.1);
          color: #ffc107;
        `;
      case 'approved':
        return `
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
        `;
      case 'rejected':
        return `
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        `;
      case 'paid':
        return `
          background: rgba(23, 162, 184, 0.1);
          color: #17a2b8;
        `;
      default:
        return `
          background: rgba(108, 117, 125, 0.1);
          color: #6c757d;
        `;
    }
  }}
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  
  a, button {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #F68B21;
      background: rgba(246, 139, 33, 0.1);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  
  .icon {
    font-size: 4rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: #495057;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
`;



const ManagerFundRequestsList: React.FC = () => {
  const { user } = useAuth();
  const [fundRequests, setFundRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  
  useEffect(() => {
    const fetchManagerFundRequests = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Get assigned projects for the current manager (backend now handles filtering)
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const managerProjects = assignedProjectsResponse.data || [];
        
        const managerProjectIds = managerProjects.map((project: any) => project._id || project.id);
        
        // Get all fund requests and filter for manager's projects
        const fundRequestsResponse = await apiService.getFundRequests();
        const allRequests = fundRequestsResponse.data || [];
        
        // Filter fund requests for manager's projects
        const managerRequests = allRequests.filter((request: any) => 
          managerProjectIds.includes(request.projectId)
        );
        
        console.log('Manager fund requests:', managerRequests);
        setFundRequests(managerRequests);
        
      } catch (error) {
        console.error('Error fetching manager fund requests:', error);
        setFundRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchManagerFundRequests();
  }, [user]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  

  
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return FaClock;
      case 'approved':
        return FaCheckCircle;
      case 'rejected':
        return FaTimesCircle;
      case 'paid':
        return FaFileInvoiceDollar;
      default:
        return FaClock;
    }
  };
  
  const filteredRequests = fundRequests.filter(request => {
    if (statusFilter !== 'all' && request.status !== statusFilter) {
      return false;
    }
    if (projectFilter !== 'all' && request.projectName !== projectFilter) {
      return false;
    }
    return true;
  });
  
  const uniqueProjects = [...new Set(fundRequests.map(r => r.projectName))];
  
  if (fundRequests.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>
            <IconWrapper icon={FaMoneyBillWave} className="icon" />
            Fund Requests
          </h1>
        </PageHeader>
        
        <EmptyState>
          <IconWrapper icon={FaMoneyBillWave} className="icon" />
          <h3>No Fund Requests</h3>
          <p>There are no fund requests for your projects yet.</p>
        </EmptyState>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHeader>
        <h1>
          <IconWrapper icon={FaMoneyBillWave} className="icon" />
          Fund Requests
        </h1>
      </PageHeader>
      
      <FiltersSection>
        <div className="filters-header">
          <h3>Filters</h3>
        </div>
        <div className="filters-grid">
          <FilterSelect 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </FilterSelect>
          
          <FilterSelect 
            value={projectFilter} 
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {uniqueProjects.map(project => (
              <option key={project} value={project}>{project}</option>
            ))}
          </FilterSelect>
        </div>
      </FiltersSection>
      
      <RequestsTable>
        <TableHeader>
          <div style={{ textAlign: 'left' }}>Request ID</div>
          <div style={{ textAlign: 'left' }}>Project</div>
          <div className="requester-column" style={{ textAlign: 'left' }}>Requester</div>
          <div className="amount-column" style={{ textAlign: 'right' }}>Amount</div>
          <div className="date-column" style={{ textAlign: 'left' }}>Date</div>
          <div style={{ textAlign: 'center' }}>Status</div>
          <div style={{ textAlign: 'center' }}>Actions</div>
        </TableHeader>
        
        {filteredRequests.map(request => (
          <TableRow key={request.id || request._id}>
            <RequestId>{request.id || request._id}</RequestId>
            <ProjectName>{request.projectName}</ProjectName>
            <Requester className="requester-column">{request.requesterId?.name || request.requester}</Requester>
            <Amount className="amount-column">{formatCurrency(request.amount)}</Amount>
            <Date className="date-column">{formatDate(request.createdAt)}</Date>
            <StatusBadge status={request.status}>
              {(() => {
                const StatusIcon = getStatusIcon(request.status);
                return <IconWrapper icon={StatusIcon} style={{ marginRight: '0.25rem' }} />;
              })()}
              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </StatusBadge>
            <Actions>
              <Link to={`/manager/fund-requests/${request.id || request._id}`} title="View Details">
                <IconWrapper icon={FaEye} />
              </Link>
              {request.approvalChain?.find((step: any) => 
                step.role === 'Project Manager' && step.status === 'Pending'
              ) && (
                <Link to={`/manager/fund-requests/${request.id || request._id}`} title="Review & Approve">
                  <IconWrapper icon={FaCheckCircle} />
                </Link>
              )}
            </Actions>
          </TableRow>
        ))}
      </RequestsTable>
    </PageContainer>
  );
};

export default ManagerFundRequestsList; 